import { EXPIRY_STATE_LABELS, EXPIRY_STATES, expiryStateSchema, type StockLot } from "@station/contracts";
import {
  Alert,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CalendarX2 } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  formatStockQuantity,
  movementSourceKey,
  quantityExport,
} from "../../../features/market/market-values.js";
import {
  type LotFilters,
  lotsQuery,
  useProductSummary,
  useStockLots,
} from "../../../features/market/queries.js";
import {
  ExpiryBadge,
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  SourceDocLink,
  useMarketStations,
  WarehouseFilterField,
} from "../../../features/market/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { formatDate } from "../../../lib/format.js";

const searchSchema = z.object({
  ...marketSearchShape,
  warehouse: z.string().uuid().optional().catch(undefined),
  product: z.string().uuid().optional().catch(undefined),
  expiry: expiryStateSchema.optional().catch(undefined),
  empty: z.enum(["true"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/market/partiler")({
  validateSearch: (search) => searchSchema.parse(search),
  component: LotsRoute,
});

/**
 * Partiler ve SKT (Prompt 16 — web): SKT takipli ürünlerin açık partileri, SKT'ye göre sıralı (FEFO — ilk biten
 * ilk çıkar). Durum istasyonun "SKT uyarı günü" ayarıyla hesaplanır: `EXPIRED` / `EXPIRING` / `OK` rozetleri
 * METİNLİDİR. "Yalnızca yaklaşanlar" hızlı süzgeci SKT durumu süzgecinin kısayoludur.
 */
function LotsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketLots")}
      description={t("market.lots.description")}
      breadcrumb={[{ label: t("menu.marketLots") }]}
    >
      {({ companyId }) => <LotsBody companyId={companyId} />}
    </MarketPage>
  );
}

function LotsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = useMarketStations(companyId);
  const product = useProductSummary(search.product);

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters: LotFilters = {
    stationId: station.stationId ?? undefined,
    warehouseId: station.stationId ? search.warehouse : undefined,
    productId: search.product,
    expiryState: search.expiry,
    includeEmpty: search.empty === "true",
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useStockLots(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<StockLot>[]>(
    () => [
      {
        id: "product",
        header: t("market.fields.product"),
        hideable: false,
        exportValue: (row) => `${row.productCode} ${row.productName}`,
        cell: (row) => (
          <Link
            to="/market/stok/$productId"
            params={{ productId: row.productId }}
            search={{ station: row.stationId }}
            className="grid gap-0.5 underline underline-offset-4"
          >
            <span>{row.productName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.productCode}</span>
          </Link>
        ),
      },
      {
        id: "lotNo",
        header: t("market.fields.lotNo"),
        exportValue: (row) => row.lotNo,
        cell: (row) => <span className="font-mono text-xs">{row.lotNo ?? t("market.lots.noLotNo")}</span>,
      },
      {
        id: "expiryDate",
        header: t("market.fields.expiryDate"),
        hideable: false,
        exportValue: (row) => row.expiryDate,
        cell: (row) => <span className="tabular">{formatDate(row.expiryDate)}</span>,
      },
      {
        id: "daysToExpiry",
        header: t("market.fields.daysToExpiry"),
        align: "right",
        exportValue: (row) => row.daysToExpiry,
        cell: (row) =>
          row.daysToExpiry === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="tabular">
              {row.daysToExpiry < 0
                ? t("market.lots.daysAgo", { count: -row.daysToExpiry })
                : t("market.lots.daysLeft", { count: row.daysToExpiry })}
            </span>
          ),
      },
      {
        id: "state",
        header: t("market.fields.expiryState"),
        exportValue: (row) => (row.expiryState ? EXPIRY_STATE_LABELS[row.expiryState] : null),
        cell: (row) => <ExpiryBadge state={row.expiryState} />,
      },
      {
        id: "quantity",
        header: t("market.fields.remaining"),
        align: "right",
        exportValue: (row) => quantityExport(row.quantity),
        cell: (row) => (
          <span className="tabular whitespace-nowrap">
            {formatStockQuantity(row.quantity)} / {formatStockQuantity(row.initialQuantity, row.unit)}
          </span>
        ),
      },
      {
        id: "warehouse",
        header: t("market.fields.warehouse"),
        exportValue: (row) => row.warehouseCode,
        cell: (row) => <span className="font-mono text-xs">{row.warehouseCode}</span>,
      },
      {
        id: "receivedAt",
        header: t("market.fields.receivedAt"),
        exportValue: (row) => row.receivedAt,
        cell: (row) => <span className="tabular">{formatDate(row.receivedAt)}</span>,
      },
      {
        id: "source",
        header: t("market.fields.source"),
        defaultVisible: false,
        exportValue: (row) => t(`market.movements.source.${movementSourceKey(row.sourceDocType)}`),
        cell: (row) => (
          <SourceDocLink
            sourceDocType={row.sourceDocType}
            sourceDocId={row.sourceDocId}
            label={t(`market.movements.source.${movementSourceKey(row.sourceDocType)}`)}
          />
        ),
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
        <WarehouseFilterField
          companyId={companyId}
          stationId={station.stationId}
          value={search.warehouse}
          onChange={(warehouse) => setSearch({ warehouse })}
        />
        {search.product && product.data ? (
          <p className="text-sm">
            <span className="text-muted-foreground">{t("market.fields.product")}: </span>
            <span className="font-medium">
              {product.data.code} {product.data.name}
            </span>{" "}
            <button
              type="button"
              className="underline underline-offset-4"
              onClick={() => setSearch({ product: undefined })}
            >
              {t("market.lots.clearProduct")}
            </button>
          </p>
        ) : null}
      </Card>

      <DataTable
        caption={t("market.lots.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<CalendarX2 />}
            title={t("market.lots.emptyTitle")}
            description={t("market.lots.emptyDescription")}
          />
        }
        filters={
          <>
            <FormField label={t("market.fields.expiryState")} className="w-48">
              {(control) => (
                <Select
                  {...control}
                  value={search.expiry ?? ""}
                  onChange={(event) => setSearch({ expiry: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {EXPIRY_STATES.map((state) => (
                    <option key={state} value={state}>
                      {EXPIRY_STATE_LABELS[state]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <label className="flex min-h-10 items-center gap-2 text-sm">
              <Checkbox
                checked={search.expiry === "EXPIRING"}
                onChange={(event) => setSearch({ expiry: event.target.checked ? "EXPIRING" : undefined })}
              />
              {t("market.lots.expiringOnly")}
            </label>
            <label className="flex min-h-10 items-center gap-2 text-sm">
              <Checkbox
                checked={search.empty === "true"}
                onChange={(event) => setSearch({ empty: event.target.checked ? "true" : undefined })}
              />
              {t("market.lots.includeEmpty")}
            </label>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<StockLot>(
            async (cursor) =>
              (
                await api.GET("/market/stock/lots", {
                  params: { query: lotsQuery(companyId, filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "skt-partiler",
            sheetName: t("menu.marketLots"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}
